import { GiftOutlined, TrophyOutlined } from '@ant-design/icons';
import { Steps, Button, message, Spin, Modal } from 'antd';
import { motion } from 'motion/react';
import ConfettiExplosion from 'react-confetti-explosion';
import './styles.scss';
import { snappy } from 'src/_shared';
import { useState, useEffect } from 'react';
import GiftImage from 'src/_shared/assets/snappy_present.png';
import { useApolloClient, gql } from '@apollo/client';
import useMyBadgeAssignments from 'src/my-rewards/hooks/useMyBadgeAssignments';

const confettiConfig = {
	force: 0.9,
	duration: 3000,
	particleCount: 300,
	width: '2000',
	height: 1600,
	colors: ['#f650a9', '#00c3ff', '#fffc00', '#00ff87', '#ff0055', '#7700ff'],
};

const QUERY_SNAPPY_GIFTS_BY_USER_ID_COMPANY_ID = gql`
	query QuerySnappyGiftsByUserIdCompanyId($userId: ID!, $companyId: ID!) {
		querySnappyGiftsByUserIdCompanyId(userId: $userId, companyId: $companyId) {
			id
			companyId
			itemType
			userId
			badgeAssignmentId
			badgeAssignment {
				id
				badgeId
				badge {
					name
					id
				}
			}
			link
			snappyCampaignTierId
			snappyGrantId
			snappyCampaignId
			externalSnappyGiftId
			firstSeenAt
			createdAt
			claimedOn
		}
	}
`;

const RewardsProgressIndicator = ({ tiers, currentUser }) => {
	const [processingTiers, setProcessingTiers] = useState(new Set());
	const [showGiftModal, setShowGiftModal] = useState(false);
	const [isExploding, setIsExploding] = useState(false);
	const [claimedGiftData, setClaimedGiftData] = useState(null);
	const [snappyGifts, setSnappyGifts] = useState([]);
	const [newlyClaimedGifts, setNewlyClaimedGifts] = useState([]);
	const client = useApolloClient();

	const { badgeList } = useMyBadgeAssignments(currentUser, false, () => {});

	const userBadgeCount = badgeList.filter((badge) => badge.badgeId).length;

	const userBadgePoints = badgeList
		.filter((badge) => badge.badgeId)
		.reduce((total, badge) => {
			const points = badge.points || badge.badge?.points || 0;
			return total + points;
		}, 0);

	const querySnappyGifts = async () => {
		if (!currentUser?.id || !currentUser?.company?.id) {
			return;
		}

		try {
			const { data } = await client.query({
				query: QUERY_SNAPPY_GIFTS_BY_USER_ID_COMPANY_ID,
				variables: {
					userId: currentUser.id,
					companyId: currentUser.company.id,
				},
				fetchPolicy: 'network-only',
			});

			setSnappyGifts(data?.querySnappyGiftsByUserIdCompanyId || []);
		} catch (error) {
			console.error('Error querying snappy gifts:', error);
		}
	};

	useEffect(() => {
		querySnappyGifts();
	}, []);

	const handleClaimGift = async (tier) => {
		if (tier?.snappyGrant?.externalSnappyId) {
			setProcessingTiers((prev) => new Set([...prev, tier.id]));
			try {
				const response = await snappy({
					type: 'createGiftsFromNewTiers',
					userId: currentUser.id,
					sendingMethod: 'link',
					recipients: [currentUser.id],
					campaignTierId: tier.id,
				});

				if (response && response.length > 0) {
					const result = response[0];
					const giftId = result.id || result.giftId;
					const giftLink = result.link || result.giftDetails?.link;

					if (giftId && giftLink) {
						setClaimedGiftData({
							id: giftId,
							snappyUrl: giftLink,
							title: 'Your Reward is Ready!',
							tierName: tier.name,
						});
						setNewlyClaimedGifts((prev) => [
							...prev,
							{ tierId: tier.id, link: giftLink },
						]);
						setShowGiftModal(true);
						setIsExploding(true);
					} else {
						message.error(result.errorMessage || 'Error claiming gift.');
						console.error('Error claiming gift:', result.errorMessage);
					}
				} else {
					message.error('Unexpected response from the server.');
					console.error('Unexpected response:', response);
				}
			} catch (error) {
				console.error('Error claiming gift', error);
				message.error('An error occurred while claiming the gift.');
			} finally {
				setProcessingTiers((prev) => {
					const newSet = new Set(prev);
					newSet.delete(tier.id);
					return newSet;
				});
			}
		}
	};

	const handleDirectSnappyLink = (snappyGift) => {
		if (snappyGift?.link) {
			window.open(snappyGift.link, '_blank');
		}
	};

	const handleGiftModalClose = () => {
		setShowGiftModal(false);
		setIsExploding(false);
		setClaimedGiftData(null);
	};

	const handleGiftRedeem = () => {
		if (claimedGiftData?.snappyUrl) {
			window.open(claimedGiftData.snappyUrl, '_blank');
		}
		handleGiftModalClose();
	};

	const items = tiers.map((tier) => {
		const isClaimable =
			tier.unlockConditionType === 'pointsEarned'
				? userBadgePoints >= tier.unlockConditionAmount
				: userBadgeCount >= tier.unlockConditionAmount;
		const newlyClaimedGift = newlyClaimedGifts.find(
			(gift) => gift.tierId === tier.id
		);
		const isProcessing = processingTiers.has(tier.id);

		const existingSnappyGift = snappyGifts.find(
			(snappyGift) => snappyGift.snappyCampaignTierId === tier.id
		);

		let button;
		let icon;
		let status;

		if (existingSnappyGift && existingSnappyGift.link) {
			button = (
				<Button
					style={{ fontSize: '15px' }}
					size="large"
					type="link"
					onClick={() => handleDirectSnappyLink(existingSnappyGift)}
				>
					<strong style={{ maxWidth: 120, textWrap: 'auto' }}>
						Finish Claiming Reward
					</strong>
				</Button>
			);
			icon = <TrophyOutlined />;
			status = 'finish';
		} else if (newlyClaimedGift) {
			button = (
				<Button
					style={{ fontSize: '15px' }}
					size="large"
					type="link"
					onClick={() => window.open(newlyClaimedGift.link, '_blank')}
				>
					<strong style={{ maxWidth: 120, textWrap: 'auto' }}>
						Finish Claiming Reward
					</strong>
				</Button>
			);
			icon = <TrophyOutlined />;
			status = 'finish';
		} else if (isClaimable) {
			button = (
				<Button
					style={{ fontSize: '15px' }}
					size="large"
					type="link"
					onClick={() => handleClaimGift(tier)}
					loading={isProcessing}
					disabled={isProcessing}
				>
					<strong>{isProcessing ? 'Processing...' : 'Claim Reward'}</strong>
				</Button>
			);
			icon = <GiftOutlined />;
			status = 'finish';
		} else {
			button = (
				<strong>
					{tier.unlockConditionAmount}{' '}
					{tier?.unlockConditionType === 'pointsEarned'
						? 'points'
						: 'Recognitions'}
				</strong>
			);
			icon = <GiftOutlined />;
			status = 'wait';
		}

		return {
			title: <p className="rewards-progress-reward-level">{tier.name}</p>,
			description: (
				<>
					<p className="rewards-progress-reward-description">
						{tier.description}
					</p>
					<p
						className="rewards-progress-reward-points"
						style={{ color: isClaimable ? 'green' : 'gray' }}
					>
						{button}
					</p>
				</>
			),
			icon: (
				<div
					className={`icon-wrapper ${isClaimable ? 'claimed' : ''}`}
					style={{
						backgroundColor: isClaimable ? '#f650a9' : 'lightgrey',
					}}
				>
					{icon}
				</div>
			),
			status,
		};
	});

	const itemIsClaimable = tiers.map((tier, index) => {
		return {
			index,
			isClaimable:
				tier.unlockConditionType === 'pointsEarned'
					? userBadgePoints >= tier.unlockConditionAmount
					: userBadgeCount >= tier.unlockConditionAmount,
		};
	});

	for (let i = 1; i < tiers.length; i++) {
		if (itemIsClaimable[i - 1].isClaimable && itemIsClaimable[i].isClaimable) {
			items[i - 1].className = 'tail-pink';
		}
	}

	const currentStepIndex = tiers.findIndex((tier) => {
		const userValue =
			tier.unlockConditionType === 'pointsEarned'
				? userBadgePoints
				: userBadgeCount;
		return userValue < tier.unlockConditionAmount;
	});

	return (
		<div>
			<Steps
				current={currentStepIndex === -1 ? tiers.length : currentStepIndex}
				labelPlacement="vertical"
				items={items}
			/>

			{/* Confetti Gift Modal */}
			<Modal
				title={null}
				open={showGiftModal}
				onCancel={handleGiftModalClose}
				footer={[
					<Button key="redeem" type="primary" onClick={handleGiftRedeem}>
						Go to Snappy
					</Button>,
					<Button key="close" onClick={handleGiftModalClose}>
						Close
					</Button>,
				]}
				centered
				width={500}
			>
				{isExploding && (
					<div
						style={{
							position: 'absolute',
							top: '50%',
							left: '50%',
							transform: 'translate(-50%, -50%)',
							zIndex: 9999,
						}}
					>
						<ConfettiExplosion {...confettiConfig} />
					</div>
				)}

				<div style={{ textAlign: 'center', padding: '20px 0' }}>
					<motion.div
						initial={{ scale: 0.8, opacity: 0 }}
						animate={{ scale: 1, opacity: 1 }}
						transition={{
							type: 'spring',
							stiffness: 200,
							damping: 20,
						}}
						style={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							gap: '20px',
						}}
					>
						<img
							src={GiftImage}
							alt="Gift"
							style={{
								width: '120px',
								height: '120px',
								objectFit: 'contain',
							}}
						/>

						<div>
							<h2
								style={{
									color: '#f650a9',
									marginBottom: '8px',
									fontSize: '24px',
								}}
							>
								{claimedGiftData?.title || 'Your Reward is Ready!'}
							</h2>
							{claimedGiftData?.tierName && (
								<p style={{ fontSize: '16px', color: '#666', margin: 0 }}>
									{claimedGiftData.tierName}
								</p>
							)}
						</div>

						<p
							style={{
								fontSize: '16px',
								color: '#666',
								textAlign: 'center',
								maxWidth: '300px',
							}}
						>
							Click "Go to Snappy" to redeem your reward!
						</p>
					</motion.div>
				</div>
			</Modal>
		</div>
	);
};

export default RewardsProgressIndicator;
